import { FC, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Table,
  type TableColumn,
  type TableRow,
  Tabs,
  TextButton,
  theme,
  Typography,
} from '@/design-system';
import { typographyVariantStyles } from '@/design-system/typography-variants';

import { CHART_AXIS_TEXT, CHART_GRID, CHART_REFERENCE, KIND_COLORS } from './chart-palette';
import {
  ChartArea,
  ChartCard,
  ChartHeader,
  ChartTooltipBox,
  LegendItem,
  LegendList,
  SectionActions,
  SectionTitle,
  SkeletonBar,
  Swatch,
  TooltipRow,
} from './styled';
import {
  ACTIVITY_KIND_LABELS,
  ACTIVITY_KINDS,
  type ActivityBucket,
  type ActivityKindKey,
  type Granularity,
  granularity as granularityValues,
} from './team-trends';

export const ACTIVITY_CHART_TITLE = 'Activity over time';
export const SHOW_TABLE_LABEL = 'Show as table';
export const SHOW_CHART_LABEL = 'Show as chart';

const CHART_HEIGHT = 280;
const MAX_BAR = 24;
const SEGMENT_GAP = 2;
const BAR_RADIUS = theme.radius[4];
const AXIS_TICK = {
  fill: CHART_AXIS_TEXT,
  fontSize: typographyVariantStyles.body4.fontSize,
};

const topKindOf = (bucket: ActivityBucket): ActivityKindKey | null =>
  [...ACTIVITY_KINDS].reverse().find((kind) => bucket[kind] > 0) ?? null;

const bottomKindOf = (bucket: ActivityBucket): ActivityKindKey | null =>
  ACTIVITY_KINDS.find((kind) => bucket[kind] > 0) ?? null;

interface SegmentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ActivityBucket;
  kind: ActivityKindKey;
}

const Segment: FC<SegmentProps> = ({ x = 0, y = 0, width = 0, height = 0, payload, kind }) => {
  if (!payload || height <= 0) return null;
  const isTop = topKindOf(payload) === kind;
  const isBottom = bottomKindOf(payload) === kind;
  const drawn = isBottom ? height : Math.max(0, height - SEGMENT_GAP);
  if (drawn <= 0) return null;
  const r = isTop ? Math.min(BAR_RADIUS, drawn, width / 2) : 0;
  const path = [
    `M${x},${y + drawn}`,
    `L${x},${y + r}`,
    r ? `Q${x},${y} ${x + r},${y}` : '',
    `L${x + width - r},${y}`,
    r ? `Q${x + width},${y} ${x + width},${y + r}` : '',
    `L${x + width},${y + drawn}`,
    'Z',
  ].join(' ');
  return <path d={path} fill={KIND_COLORS[kind]} />;
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: ActivityBucket }>;
  clickable: boolean;
}

const ChartTooltip: FC<ChartTooltipProps> = ({ active, payload, clickable }) => {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  return (
    <ChartTooltipBox>
      <Typography variant="body3" fontWeight={600} color={theme.colors.text.primary}>
        {bucket.label}
      </Typography>
      {[...ACTIVITY_KINDS].reverse().map((kind) => (
        <TooltipRow key={kind}>
          <LegendItem as="span">
            <Swatch color={KIND_COLORS[kind]} />
            <Typography variant="body4" color={theme.colors.text.secondary}>
              {ACTIVITY_KIND_LABELS[kind]}
            </Typography>
          </LegendItem>
          <Typography variant="body4" color={theme.colors.text.primary}>
            {bucket[kind].toLocaleString()}
          </Typography>
        </TooltipRow>
      ))}
      <TooltipRow>
        <Typography variant="body4" fontWeight={600} color={theme.colors.text.primary}>
          Entries
        </Typography>
        <Typography variant="body4" fontWeight={600} color={theme.colors.text.primary}>
          {bucket.entries.toLocaleString()}
        </Typography>
      </TooltipRow>
      <TooltipRow>
        <Typography variant="body4" color={theme.colors.text.secondary}>
          Contacts · Deals created · advanced
        </Typography>
        <Typography variant="body4" color={theme.colors.text.primary}>
          {`${bucket.contactsReached} · ${bucket.dealsCreated} · ${bucket.dealsAdvanced}`}
        </Typography>
      </TooltipRow>
      {clickable && (
        <Typography variant="body4" color={theme.colors.text.tertiary}>
          Click to see the entries
        </Typography>
      )}
    </ChartTooltipBox>
  );
};

interface BucketRow extends TableRow {
  bucket: ActivityBucket;
}

const TABLE_COLUMNS: TableColumn<BucketRow>[] = [
  {
    key: 'label',
    header: 'Period',
    minWidth: 140,
    cell: ({ bucket }) => <Typography variant="body3">{bucket.label}</Typography>,
  },
  ...(
    [...ACTIVITY_KINDS, 'entries', 'contactsReached', 'dealsCreated', 'dealsAdvanced'] as const
  ).map(
    (key): TableColumn<BucketRow> => ({
      key,
      header:
        key === 'entries'
          ? 'Entries'
          : key === 'contactsReached'
            ? 'Contacts'
            : key === 'dealsCreated'
              ? 'Deals created'
              : key === 'dealsAdvanced'
                ? 'Deals advanced'
                : ACTIVITY_KIND_LABELS[key],
      align: 'right',
      width: 104,
      cell: ({ bucket }) => <Typography variant="body3">{bucket[key].toLocaleString()}</Typography>,
    }),
  ),
];

interface ActivityChartProps {
  buckets: ActivityBucket[] | null;
  granularity: Granularity;
  onGranularityChange: (_next: Granularity) => void;
  previousAverage: number | null;
  onSelectDay?: (_day: string) => void;
}

export const ActivityChart: FC<ActivityChartProps> = ({
  buckets,
  granularity,
  onGranularityChange,
  previousAverage,
  onSelectDay,
}) => {
  const [asTable, setAsTable] = useState(false);
  const clickable = granularity === granularityValues.Day && !!onSelectDay;
  const rows = useMemo(
    () => (buckets ?? []).map((bucket): BucketRow => ({ key: bucket.key, bucket })),
    [buckets],
  );

  const handleClick = (state: { activePayload?: ReadonlyArray<{ payload?: ActivityBucket }> }) => {
    const bucket = state?.activePayload?.[0]?.payload;
    if (clickable && bucket) onSelectDay?.(bucket.key);
  };

  return (
    <ChartCard aria-label={ACTIVITY_CHART_TITLE}>
      <ChartHeader>
        <SectionTitle>
          <Typography variant="body1" fontWeight={600}>
            {ACTIVITY_CHART_TITLE}
          </Typography>
          <LegendList aria-label="Legend">
            {ACTIVITY_KINDS.map((kind) => (
              <LegendItem key={kind}>
                <Swatch color={KIND_COLORS[kind]} />
                <Typography variant="body4" color={theme.colors.text.secondary}>
                  {ACTIVITY_KIND_LABELS[kind]}
                </Typography>
              </LegendItem>
            ))}
            {previousAverage !== null && (
              <LegendItem>
                <Swatch color={CHART_REFERENCE} />
                <Typography variant="body4" color={theme.colors.text.secondary}>
                  Previous period average
                </Typography>
              </LegendItem>
            )}
          </LegendList>
        </SectionTitle>
        <SectionActions>
          <TextButton color="neutral" onClick={() => setAsTable((current) => !current)}>
            {asTable ? SHOW_CHART_LABEL : SHOW_TABLE_LABEL}
          </TextButton>
          <Tabs.Root
            value={granularity}
            onValueChange={(value) => onGranularityChange(value as Granularity)}
            size="sm"
            variant="boxed"
          >
            <Tabs.List>
              <Tabs.Tab value={granularityValues.Day} label="Days" />
              <Tabs.Tab value={granularityValues.Week} label="Weeks" />
            </Tabs.List>
          </Tabs.Root>
        </SectionActions>
      </ChartHeader>

      {!buckets ? (
        <SkeletonBar h={CHART_HEIGHT} />
      ) : buckets.length === 0 ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          Daily figures appear once the period has started.
        </Typography>
      ) : asTable ? (
        <Table columns={TABLE_COLUMNS} rows={rows} size="sm" />
      ) : (
        <ChartArea h={CHART_HEIGHT} clickable={clickable}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={buckets}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              barCategoryGap="20%"
              onClick={handleClick}
            >
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeWidth={1} />
              <XAxis
                dataKey="label"
                tick={AXIS_TICK}
                tickFormatter={(label: string) => label.replace('Week of ', '')}
                axisLine={{ stroke: CHART_GRID }}
                tickLine={false}
                minTickGap={16}
              />
              <YAxis
                tick={AXIS_TICK}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={40}
              />
              <Tooltip
                cursor={{ fill: theme.colors.background.overlay, opacity: 0.4 }}
                content={<ChartTooltip clickable={clickable} />}
              />
              {ACTIVITY_KINDS.map((kind) => (
                <Bar
                  key={kind}
                  dataKey={kind}
                  stackId="kinds"
                  maxBarSize={MAX_BAR}
                  fill={KIND_COLORS[kind]}
                  isAnimationActive={false}
                  shape={(props: Omit<SegmentProps, 'kind'>) => <Segment {...props} kind={kind} />}
                />
              ))}
              {previousAverage !== null && previousAverage > 0 && (
                <ReferenceLine
                  y={previousAverage}
                  stroke={CHART_REFERENCE}
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartArea>
      )}
    </ChartCard>
  );
};
